import { useState } from "react";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { mfgCatalogApi, useMfgItemTypes, type CreateMfgItemBody } from "@/lib/mfg-catalog";
import { ApiError } from "@/lib/api";

const SUBTYPE_BEARING = new Set(["Material", "Waste", "Variant", "Alternative"]);

export function CreateMfgItemForm() {
  const navigate = useNavigate();
  const { data: types } = useMfgItemTypes();
  const [type, setType] = useState("");
  const [name, setName] = useState("");
  const [itemNumber, setItemNumber] = useState("");

  const create = useMutation({
    mutationFn: () => {
      const body: CreateMfgItemBody = {
        itemTypeCode: type, name, itemNumber: itemNumber || null, description: null, unitOfMeasureCode: null,
        material: type === "Material" ? { width: null, length: null, thickness: null, materialGrainCode: null, unitOfMeasureCode: null } : null,
        waste: type === "Waste" ? { itemCutoutMarginMm: null, machineGrabWidthMm: null, machineGrabLengthMm: null, startRunLengthMm: null, endRunLengthMm: null, percentageWaste: null } : null,
        variant: type === "Variant" ? { name: null, minimumChoice: 1, maximumChoice: 1 } : null,
        alternative: type === "Alternative" ? { name: null } : null,
      };
      return mfgCatalogApi.create(body);
    },
    onSuccess: (created) => { toast.success("Item created"); void navigate(`/admin/mfg-catalog/${created.mfgItemId}`); },
    onError: (e) => toast.error(e instanceof ApiError && e.status === 400 ? "Invalid — check the fields." : `Create failed: ${(e).message}`),
  });

  return (
    <div className="space-y-3">
      <div><Label>Type</Label>
        <select className="h-9 w-full rounded-md border px-2 text-sm" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">Choose a type…</option>
          {(types ?? []).map((t) => <option key={t.mfgItemTypeCode} value={t.mfgItemTypeCode}>{t.name}</option>)}
        </select>
      </div>
      <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div><Label>Item #</Label><Input value={itemNumber} onChange={(e) => setItemNumber(e.target.value)} /></div>
      {type && SUBTYPE_BEARING.has(type) && <p className="text-xs text-muted-foreground">Geometry/config for this {type} is added on the next screen after creation.</p>}
      <Button onClick={() => create.mutate()} disabled={create.isPending || !type || !name.trim()}>Create item</Button>
    </div>
  );
}
